import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useTheme } from '@/lib/hooks'
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary'

import { addToCart } from './store/checkoutSlice'
import ConfirmationModal from './components/ConfirmationModal/ConfirmationModal'
import KiosksBar from './components/KiosksBar/KiosksBar'
import LeftPanel from './components/LeftPanel/LeftPanel'
import PaymentModal from './components/PaymentModal/PaymentModal'
import RightPanel from './components/RightPanel/RightPanel'
import TopBar from './components/TopBar/TopBar'
import { DeskContent, DeskLeftPanel, DeskRightPanel, DeskWrapper } from './CashierDesk.styles'

const CashierDesk = () => {
  const dispatch = useDispatch()
  const themes = useTheme()
  const currentModal = useSelector(state => state.checkout.currentModal)
  const wrapperRef = useRef(null)

  // Log mount + viewport/layout diagnostics
  useEffect(() => {
    console.log('[CASHIER] CashierDesk MOUNTED')
    console.log('[CASHIER] Viewport:', window.innerWidth, 'x', window.innerHeight)
    console.log('[CASHIER] Screen:', window.screen.width, 'x', window.screen.height)
    console.log('[CASHIER] DevicePixelRatio:', window.devicePixelRatio)
    console.log('[CASHIER] UserAgent:', navigator.userAgent)
    console.log('[CASHIER] Products loaded:', themes.products?.length || 0)
    console.log('[CASHIER] AndroidBridge:', !!window.AndroidBridge)
    console.log('[CASHIER] BtApp:', !!window.BtApp)

    // Check actual rendered sizes after paint
    requestAnimationFrame(() => {
      const wrapper = wrapperRef.current
      if (wrapper) {
        const rect = wrapper.getBoundingClientRect()
        console.log('[CASHIER] DeskWrapper rect:', JSON.stringify({ w: rect.width, h: rect.height }))
        const content = wrapper.querySelector('[data-testid="desk-content"]')
        if (content) {
          const cRect = content.getBoundingClientRect()
          console.log('[CASHIER] DeskContent rect:', JSON.stringify({ w: cRect.width, h: cRect.height }))
          if (cRect.height < 50) {
            console.warn('[CASHIER] WARNING: DeskContent has very small height! TopBar+KiosksBar may consume all space.')
          }
        }
      }
    })
  }, [themes])

  // Auto-add first product 5s after launch for testing sync
  const productsRef = useRef(themes.products)
  productsRef.current = themes.products
  useEffect(() => {
    const timer = setTimeout(() => {
      const products = productsRef.current
      if (products?.length > 0) {
        const product = products[0]
        console.log('[CASHIER] Auto-adding first product for test:', product.title)
        dispatch(addToCart(product))
      }
    }, 5000)
    return () => clearTimeout(timer)
  }, [dispatch])

  // BtApp.handleQrReader scanner support (same pattern as action-pos)
  // Android calls javascript:BtApp.handleQrReader('...')
  useEffect(() => {
    if (!window.BtApp) {
      window.BtApp = {}
    }

    window.BtApp.handleQrReader = (payload) => {
      console.log('[SCANNER] QR/Barcode scanned:', payload)
      let product = themes.products.find(
        o => o.id?.toString() === payload?.toString() ||
             o.barcode === payload?.toString()
      )
      if (!product) {
        const idx = Math.floor(Math.random() * themes.products.length)
        product = themes.products[idx]
        console.log('[SCANNER] No product found for code:', payload, '— picking random:', product.title)
      } else {
        console.log('[SCANNER] Product found, adding to cart:', product.title)
      }
      dispatch(addToCart(product))
      return product
    }

    console.log('[CASHIER] BtApp.handleQrReader registered')

    return () => {
      if (window.BtApp) {
        window.BtApp.handleQrReader = () => {}
      }
    }
  }, [dispatch, themes.products])

  return (
    <DeskWrapper ref={wrapperRef}>
      <ErrorBoundary name="TopBar">
        <TopBar />
      </ErrorBoundary>
      <DeskContent data-testid="desk-content">
        <DeskLeftPanel>
          <ErrorBoundary name="LeftPanel">
            <LeftPanel />
          </ErrorBoundary>
        </DeskLeftPanel>
        <DeskRightPanel>
          <ErrorBoundary name="RightPanel">
            <RightPanel />
          </ErrorBoundary>
        </DeskRightPanel>
      </DeskContent>
      <ErrorBoundary name="KiosksBar">
        <KiosksBar />
      </ErrorBoundary>
      {currentModal === 'PAYMENT' && <PaymentModal />}
      {currentModal === 'CONFIRMATION' && <ConfirmationModal />}
    </DeskWrapper>
  )
}

export default CashierDesk
